import { z } from 'zod';
import { baseDocumentFields, moneyCents, objectIdString } from './common.js';
import {
  creditStatus,
  incomeSource,
  maritalStatus,
  timeline,
} from './enums.js';
import { computedQualification } from './qualification.js';

// Snapshot of every answer the applicant gave. Field names are stable;
// extra/ad-hoc questions go into `customAnswers` so we don't need to migrate
// schemas when the broker adds a new question.
export const qualificationAnswers = z
  .object({
    monthlyIncomeCents: moneyCents,
    incomeSource: incomeSource,
    /** Asked only when incomeSource is self_employed · autônomos/MEI precisam
     * comprovar renda por IR ou DECORE, então a Caixa trata o lead muito
     * diferente dependendo dessa resposta. Opcional pra não quebrar leads
     * antigos (que não tinham esse campo). */
    hasIncomeTaxDeclaration: z.boolean().optional(),
    age: z.number().int().min(18).max(110),
    maritalStatus: maritalStatus,
    dependents: z.number().int().min(0).max(20),
    hasFGTS: z.boolean(),
    fgtsBalanceCents: moneyCents.optional(),
    ownsProperty: z.boolean(),
    hasUsedMCMVBefore: z.boolean(),
    creditStatus: creditStatus,
    downPaymentAvailableCents: moneyCents.optional(),
    timeline: timeline,
    customAnswers: z
      .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
      .default({}),
  })
  .strict();

export type QualificationAnswers = z.infer<typeof qualificationAnswers>;

// Generic answers shape for non-MCMV verticals. Each vertical declares its
// own FormConfig and the engine validates the inbound answers against
// buildAnswersSchema(config) before they hit Mongo. Stored loosely so that
// adding a vertical never requires a schema migration on this collection.
export const genericAnswers = z.record(z.string(), z.unknown());
export type GenericAnswers = z.infer<typeof genericAnswers>;

// Immutable per-submission snapshot. Multiple submissions per lead are allowed
// (the same person re-fills the form later), so this is a historical record.
//
// `answers` is loose at the persistence layer (`genericAnswers`) · vertical-
// specific shape is enforced at the API boundary and again by the qualifier
// for that vertical. Stored docs from before multi-vertical happen to match
// `qualificationAnswers` (MCMV) and can be parsed back to that shape.
export const qualificationSubmission = z
  .object({
    ...baseDocumentFields,
    leadId: objectIdString,
    propertyId: objectIdString.optional(),
    brokerId: objectIdString,
    /**
     * Vertical key snapshotted at submit time. Optional during rollout ·
     * legacy submissions without it are MCMV by convention.
     */
    verticalKey: z.string().min(2).max(40).optional(),
    answers: genericAnswers,
    computedQualification: computedQualification,
    submittedAt: z.coerce.date(),
  })
  .strict();

export type QualificationSubmission = z.infer<typeof qualificationSubmission>;
